import React, { useEffect, useState } from "react"
import SideBarLayout from "src/v2/layout/side-bar-layout"
import { Box } from "@mui/system"
import theme from "src/v2/theme/theme"
import { Grid } from "@mui/material"
import CompanyTabsWrappedLabel from "./components/company-tab/company-tab"
import { BoxDataCompany } from "./components/box-data-company/box-data-company"
import CompanyEmptyState from "./components/company-empty-state/company-empty-state"
import DatalakeHeaderEnterprise from "./components/header/Header"
import GenericModal from "src/v2/components/modal/generic-modal"
import DataGridTable from "src/v2/components/Table/data-grid-table"
import DatalakeService from "src/services/datalake-service"
import {MotorEmpresasService} from "src/services/motor-empresas-service"
import LoaderOverlay from "src/v2/components/LoaderOverlay/loader-overlay"
import Loader from "src/v2/components/Loader/loader"
import { ReactComponent as TableBaseIcon } from "src/v2/assets/icons/table-base-icon.svg"
import ButtonWithIcon from "src/v2/components/Button/Button"
import { TokenService } from "src/services/token-service"
import useCompanyAutocomplete from "./hooks/use-company-autocomplete"
import EmissionTable from "./components/data-emission-table/emission-table"
import { COLUMNS_MODAL_TABLE_CNPJ } from "./constants/full-list-cnpj-const.js"

export const DataLakeCompany = () => {
    const tokenService = new TokenService()
    const datalakeService = new DatalakeService()
    const motorEmpresasService = new MotorEmpresasService()

    const isAdminUser = tokenService.verifyUserIsAdmin()

    const [companyList, setCompanyList] = useState([])
    const [openModal, setOpenModal] = useState(false)
    const [loadingCompanyList, setLoadingCompanyList] = useState(false)
    const [cpfCnpjSelected, setCpfCnpjSelected] = useState(null)
    const [companyInfo, setCompanyInfo] = useState({})
    const [loadingCompanyInfo, setLoadingCompanyInfo] = useState(false)
    const [emissionData, setEmissionData] = useState([])
    const [loadingEmissions, setLoadingEmissions] = useState(false)
    //const [dataPD, setDataPD] = useState({})
    //const [loadingPd, setLoadingPd] = useState(false)
    const companyAutocomplete = useCompanyAutocomplete({
        companies: companyList,
        companiesLoading: loadingCompanyList,
        onCompanySelect: setCpfCnpjSelected,
    })

    useEffect(() => {
        const loadCompanies = async () => {
            setLoadingCompanyList(true)

            try {
                const data = await datalakeService.loadList()
                setCompanyList(data?.body?.results ?? [])
            } catch (error) {
                console.error(
                    "Erro ao carregar empresas:",
                    error
                )
                setCompanyList([])
            } finally {
                setLoadingCompanyList(false)
            }
        }

        loadCompanies()
    }, [])

    useEffect(() => {
        const cpfCnpj = cpfCnpjSelected?.CPFCNPJ

        if (!cpfCnpj) {
            return
        }

        const loadCompanyInfo = async () => {
            setOpenModal(false)
            setLoadingCompanyInfo(true)

            try {
                const data = await datalakeService.loadInfo(cpfCnpj)
                setCompanyInfo(data?.body ?? {})
            } catch (error) {
                console.error(
                    "Erro ao carregar informações da empresa:",
                    error
                )
                setCompanyInfo({})
            } finally {
                setLoadingCompanyInfo(false)
            }
        }

        loadCompanyInfo()
    }, [cpfCnpjSelected?.CPFCNPJ])

    useEffect(() => {
        const cpfCnpj = cpfCnpjSelected?.CPFCNPJ

        if (!cpfCnpj) {
            setEmissionData([])
            return
        }

        let isCurrentRequest = true

        const loadEmissions = async () => {
            setLoadingEmissions(true)

            try {
                const response =
                    await motorEmpresasService.loadEmissoes(cpfCnpj)

                const rows =
                    response?.body?.results ??
                    response?.body?.data ??
                    []

                if (isCurrentRequest) {
                    setEmissionData(
                        Array.isArray(rows) ? rows : []
                    )
                }
            } catch (error) {
                console.error(
                    "Erro ao carregar emissões da empresa:",
                    error
                )

                if (isCurrentRequest) {
                    setEmissionData([])
                }
            } finally {
                if (isCurrentRequest) {
                    setLoadingEmissions(false)
                }
            }
        }

        loadEmissions()

        return () => {
            isCurrentRequest = false
        }
    }, [cpfCnpjSelected?.CPFCNPJ])

    const handleOpenModal = () => {
        setOpenModal(true)
    }

    const handleCloseModal = () => {
        setOpenModal(false)
    }

    return (
        <SideBarLayout title="Empresas">
            {(loadingCompanyInfo) && (
                <LoaderOverlay>
                    <Loader />
                </LoaderOverlay>
            )}
            <GenericModal
                isOpen={openModal}
                onClose={handleCloseModal}
                title="Lista de Empresas"
                width="1000px"
            >
                <DataGridTable
                    columns={COLUMNS_MODAL_TABLE_CNPJ}
                    rows={companyList}
                    loading={loadingCompanyList}
                    pageSize={25}
                    disableDeleteColumn={true}
                    getRowId={(row, index) => {
                        if (row.CPFCNPJ) return String(row.CPFCNPJ)
                        if (row.RazaoSocial)
                            return row.RazaoSocial.replace(/\W+/g, "_")
                        return `row_${index}`
                    }}
                    onRowClick={(params) => {
                        setCpfCnpjSelected(params.row)
                        setOpenModal(false)
                    }}
                />
            </GenericModal>
            <Grid
                sx={{
                    background: theme.palette.background.primary,
                    height: "100%",
                    width: "100%",
                }}
            >
                <Box
                    sx={{
                        position: "absolute",
                        top: 0,
                        height: "199px",
                        left: {
                            xxxs: 0,
                            lg: 209,
                        },
                        right: 0,
                        zIndex: 0,
                    }}
                >
                    <DatalakeHeaderEnterprise
                        options={companyAutocomplete.state.results}
                        selectedValue={companyAutocomplete.state.selectedValue}
                        inputValue={companyAutocomplete.state.inputValue}
                        loading={loadingCompanyList || companyAutocomplete.state.loading}
                        error={companyAutocomplete.state.error}
                        onInputChange={companyAutocomplete.handlers.onInputChange}
                        onSelectionChange={companyAutocomplete.handlers.onSelectionChange}
                        getOptionLabel={companyAutocomplete.handlers.getOptionLabel}
                        isOptionEqualToValue={companyAutocomplete.handlers.isOptionEqualToValue}
                        handleClickFullList={handleOpenModal}
                    />
                </Box>
                {!cpfCnpjSelected?.CPFCNPJ ? (
                    <Box
                        sx={{
                            display: "flex",
                            position: "relative",
                            mt: { xxxs: 36, md: 30, lg: 22 },
                            width: "100%",
                            background: "#272D42",
                            flexDirection: {
                                xxxs: "column",
                            },
                        }}
                    >
                        <CompanyEmptyState
                            handleClickFullList={handleOpenModal}
                        />
                    </Box>
                ) : (
                    <Box
                        sx={{
                            display: "flex",
                            position: "relative",
                            mt: { xxxs: 36, md: 30, lg: 22 },
                            width: "100%",
                            background: "#272D42",
                            flexDirection: {
                                xxxs: "column",
                            },
                        }}
                    >
                        <Box>
                            <CompanyTabsWrappedLabel
                                companyTabChildren={
                                    <Box
                                        sx={{
                                            width: "100%",
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: 3,
                                            p: 3,
                                        }}
                                    >
                                        <BoxDataCompany
                                            companyData={companyInfo}
                                        />

                                        {isAdminUser && (
                                            <Box>
                                                <Box
                                                    display="flex"
                                                    gap={3}
                                                    flexDirection="column"
                                                >
                                                    <Box
                                                        display="flex"
                                                        flexDirection={{
                                                            xxxs: "column",
                                                            xs: "row",
                                                        }}
                                                        flexWrap={{
                                                            xs: "wrap",
                                                            md: "nowrap",
                                                        }}
                                                        columnGap={3}
                                                        rowGap={{
                                                            xxxs: 2,
                                                            md: 0,
                                                        }}
                                                    >
                                                        <ButtonWithIcon
                                                            icon={<TableBaseIcon />}
                                                        >
                                                            CONTRAPARTE
                                                        </ButtonWithIcon>

                                                        <ButtonWithIcon
                                                            icon={<TableBaseIcon />}
                                                        >
                                                            CADASTRO CVM
                                                        </ButtonWithIcon>

                                                        <ButtonWithIcon
                                                            icon={<TableBaseIcon />}
                                                        >
                                                            CADASTRO BACEN
                                                        </ButtonWithIcon>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        )}
                                    </Box>
                                }
                                emissionTabChildren={
                                    <Box
                                        sx={{
                                            width: "100%",
                                            p: 3,
                                            boxSizing: "border-box",
                                        }}
                                    >
                                        <EmissionTable
                                            data={emissionData}
                                            loading={loadingEmissions}
                                            isAdminUser={isAdminUser}
                                        />
                                    </Box>
                                }
                            />
                        </Box>
                    </Box>
                )}
            </Grid>
        </SideBarLayout>
    )
}

export default DataLakeCompany
